import { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Film } from 'lucide-react';

export default function FileUpload({
  label,
  onChange,
  accept = 'image/*',
  multiple = false,
  error,
  preview = true,
  className = '',
}) {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const inputRef = useRef(null);

  const MAX_SIZE = 60 * 1024 * 1024;

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files).filter((file) => {
      if (file.size > MAX_SIZE) {
        alert(`Файл "${file.name}" превышает 60MB и не будет загружен.`);
        return false;
      }
      return true;
    });
    setFiles(selectedFiles);

    if (preview) {
      const newPreviews = selectedFiles.map((file) => {
        if (file.type.startsWith('image/')) {
          return URL.createObjectURL(file);
        }
        return null;
      });
      setPreviews(newPreviews);
    }

    onChange(multiple ? selectedFiles : selectedFiles[0]);
  };

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    setFiles(newFiles);
    setPreviews(newPreviews);
    onChange(multiple ? newFiles : null);
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          error ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-primary-500 hover:bg-primary-50'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileChange}
          className="hidden"
        />
        <Upload className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2 text-sm text-gray-600">
          Нажмите для загрузки или перетащите файлы
        </p>
        <p className="mt-1 text-xs text-gray-500">
          {accept === 'image/*' ? 'PNG, JPG, GIF до 10MB' : 'MP4, MOV и другие видео до 60MB'}
        </p>
      </div>

      {previews.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-4">
          {previews.map((src, index) => (
            <div key={index} className="relative">
              {src ? (
                <img
                  src={src}
                  alt={`Preview ${index + 1}`}
                  className="h-24 w-full object-cover rounded-lg"
                />
              ) : (
                <div className="h-24 w-full bg-gray-100 rounded-lg flex items-center justify-center">
                  <ImageIcon className="h-8 w-8 text-gray-400" />
                </div>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {!preview && files.length > 0 && (
        <ul className="mt-3 space-y-1">
          {files.map((file, index) => (
            <li
              key={index}
              className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Film className="h-4 w-4 text-primary-500 shrink-0" />
                <span className="truncate text-gray-700">{file.name}</span>
                <span className="text-gray-400 shrink-0">
                  {(file.size / (1024 * 1024)).toFixed(1)} МБ
                </span>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
                className="ml-2 text-red-400 hover:text-red-600 shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
